import React from 'react';

// Partner Benefits Component
function PartnerBenefits() {
    const benefits = [
        {
            title: "Carbon Offsetting",
            icon: "fa-leaf",
            description: "Offset your company's carbon footprint with verified carbon credits from sustainable computing initiatives."
        },
        {
            title: "Brand Visibility",
            icon: "fa-bullhorn",
            description: "Showcase your commitment to sustainability with co-branded initiatives."
        },
        {
            title: "Tech Access",
            icon: "fa-microchip",
            description: "Priority access to our sustainable computing technologies and research."
        },
        {
            title: "Impact Reporting",
            icon: "fa-file-chart-line",
            description: "Detailed impact reports for your sustainability communications."
        }
    ];

    return (
        <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">Partner <span className="text-green-400">Benefits</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => (
                    <div key={index} className="bg-gradient-to-b from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-8 border border-gray-700 text-center transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-green-500/30">
                        <div className="w-20 h-20 bg-gradient-to-br from-green-500/30 to-green-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className={`fas ${benefit.icon} text-green-400 text-2xl`}></i>
                        </div>
                        <h3 className="text-xl font-bold mb-4">{benefit.title}</h3>
                        <p className="text-gray-300">{benefit.description}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Carbon Credits Component
function CarbonCreditsOffering({ onRequestInfo }) {
    const carbonPricing = {
        price: "65",
        minPurchase: 10,
        features: [
            "Third-party verified carbon credits",
            "Detailed impact certificate",
            "Project-specific traceability",
            "Corporate ESG reporting compatibility",
            "Annual carbon offset statement"
        ],
        packages: [
            { tons: 25, label: "Small Business", discount: "0%" },
            { tons: 100, label: "Medium Business", discount: "5%" },
            { tons: 500, label: "Enterprise", discount: "10%" },
            { tons: 1000, label: "Corporate", discount: "15%" }
        ]
    };

    return (
        <div>
            <div className="bg-gradient-to-b from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700 max-w-3xl mx-auto transform transition-all duration-300 hover:shadow-xl hover:shadow-green-500/10 hover:border-green-500/30">
                <div className="p-8">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold">Carbon Credits</h3>
                        <div className="bg-gradient-to-r from-green-500/30 to-green-400/30 px-6 py-3 rounded-full">
                            <span className="text-green-400 font-bold text-xl">£{carbonPricing.price}</span>
                            <span className="text-green-400">/ton</span>
                        </div>
                    </div>
                    
                    <p className="text-gray-300 mb-6 text-lg">
                        Our carbon credits are sourced from verified sustainable technology projects that reduce CO₂ emissions while supporting innovation.
                    </p>
                    
                    <div className="bg-gray-700/50 p-5 rounded-lg mb-6 border border-gray-600/50">
                        <p className="font-medium flex items-center">
                            <i className="fas fa-info-circle text-blue-400 mr-2"></i>
                            Minimum purchase: {carbonPricing.minPurchase} tons
                        </p>
                    </div>
                    
                    <h4 className="font-bold mb-4 text-lg flex items-center">
                        <i className="fas fa-check-circle text-green-400 mr-2"></i>
                        All carbon credits include:
                    </h4>
                    <ul className="space-y-3 mb-8 bg-gray-800/50 p-5 rounded-lg">
                        {carbonPricing.features.map((feature, index) => (
                            <li key={index} className="flex items-center">
                                <i className="fas fa-check text-green-400 mr-3"></i>
                                <span className="text-gray-300">{feature}</span>
                            </li>
                        ))}
                    </ul>
                    
                    <h4 className="font-bold mb-4 text-lg flex items-center">
                        <i className="fas fa-tags text-green-400 mr-2"></i>
                        Volume Pricing:
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        {carbonPricing.packages.map((pkg, index) => (
                            <div key={index} className="bg-gray-700/50 p-4 rounded-lg text-center border border-gray-600/30 hover:border-green-500/30 transition-all hover:bg-gray-700/80">
                                <p className="font-bold text-lg">{pkg.tons}+ tons</p>
                                <p className="text-sm text-gray-300">{pkg.label}</p>
                                <p className="text-green-400 font-bold">{pkg.discount} discount</p>
                            </div>
                        ))}
                    </div>
                    
                    <button 
                        onClick={() => onRequestInfo('carbon-credits')}
                        className="w-full py-3 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white transition-all"
                    >
                        Purchase Carbon Credits
                    </button>
                </div>
            </div>
        </div>
    );
}

// Sequoia Tons Component
function SequoiaTonsOffering({ onRequestInfo }) {
    const sequoiaTiers = [
        {
            id: "standard",
            name: "Standard Commitment",
            timespan: "25-year",
            price: "85",
            features: [
                "25-year carbon sequestration guarantee",
                "Basic impact certificate",
                "Annual project updates",
                "Company listing on conservation page",
                "Digital badge for your website"
            ]
        },
        {
            id: "enhanced",
            name: "Enhanced Commitment",
            timespan: "50-year",
            price: "125",
            popular: true,
            features: [
                "50-year carbon sequestration guarantee",
                "Enhanced impact certificate",
                "Semi-annual detailed project reports",
                "Featured partner story on our website",
                "Co-branded sustainability assets",
                "Conservation area naming rights (for bulk purchases)"
            ]
        },
        {
            id: "legacy",
            name: "Legacy Commitment",
            timespan: "100-year",
            price: "195",
            features: [
                "100-year carbon sequestration guarantee",
                "Premium impact certificate and plaque",
                "Quarterly detailed project reports",
                "On-site recognition at sequoia forest",
                "Executive tour of conservation site",
                "Custom impact storytelling and media assets",
                "Legacy conservation naming rights"
            ]
        }
    ];

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {sequoiaTiers.map((tier) => (
                    <div key={tier.id} className={`bg-gradient-to-b from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${tier.popular ? 'ring-2 ring-green-400 shadow-lg shadow-green-500/20' : 'border border-gray-700'}`}>
                        {tier.popular && (
                            <div className="bg-gradient-to-r from-green-600 to-green-400 text-white text-center py-2 font-medium">
                                Most Popular
                            </div>
                        )}
                        <div className="p-8">
                            <h3 className="text-2xl font-bold mb-4">{tier.name}</h3>
                            <div className="bg-green-900/30 text-center py-3 px-4 rounded-lg mb-6 border border-green-900/50">
                                <span className="text-green-400 font-bold text-lg">{tier.timespan} Commitment</span>
                            </div>
                            <div className="flex items-baseline mb-6">
                                <span className="text-3xl font-bold">£{tier.price}</span>
                                <span className="text-gray-400 ml-2">/ton</span>
                            </div>
                            <ul className="space-y-3 mb-8 bg-gray-800/50 p-4 rounded-lg">
                                {tier.features.map((feature, index) => (
                                    <li key={index} className="flex items-center">
                                        <i className="fas fa-check text-green-400 mr-3"></i>
                                        <span className="text-gray-300">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            <button 
                                onClick={() => onRequestInfo(`sequoia-${tier.id}`)}
                                className={`w-full py-3 rounded-lg ${
                                    tier.popular 
                                        ? 'bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 text-white' 
                                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                                } transition-all`}
                            >
                                Purchase {tier.name}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="mt-12 bg-gradient-to-b from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl p-8 border border-gray-700 shadow-lg">
                <h3 className="text-2xl font-bold mb-6 text-center">About Our Sequoia Conservation Commitments</h3>
                <p className="text-gray-300 mb-8 text-center max-w-3xl mx-auto">
                    Our Sequoia Tons program offers different commitment periods for carbon sequestration, allowing you to choose the timeframe that aligns with your organization's sustainability goals:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all hover:shadow-lg hover:shadow-green-500/5">
                        <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <i className="fas fa-seedling text-green-400 text-2xl"></i>
                        </div>
                        <p className="font-bold text-xl mb-3 text-center">25-Year Commitment</p>
                        <p className="text-sm text-gray-300">Our entry-level commitment ensures carbon remains sequestered for at least 25 years, with regular monitoring and verification.</p>
                    </div>
                    <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all hover:shadow-lg hover:shadow-green-500/5">
                        <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <i className="fas fa-tree text-green-400 text-2xl"></i>
                        </div>
                        <p className="font-bold text-xl mb-3 text-center">50-Year Commitment</p>
                        <p className="text-sm text-gray-300">Our enhanced commitment includes additional protections and management to ensure carbon remains sequestered for half a century.</p>
                    </div>
                    <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all hover:shadow-lg hover:shadow-green-500/5">
                        <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <i className="fas fa-mountain text-green-400 text-2xl"></i>
                        </div>
                        <p className="font-bold text-xl mb-3 text-center">100-Year Commitment</p>
                        <p className="text-sm text-gray-300">Our legacy commitment provides the highest level of protection, ensuring sequestered carbon remains in place for a century or more.</p>
                    </div>
                </div>
                
                <div className="bg-blue-900/20 border border-blue-700/30 p-5 rounded-xl">
                    <p className="text-gray-300 text-center">
                        <i className="fas fa-info-circle text-blue-400 mr-2"></i>
                        All commitment levels include legal protections, regular monitoring, and verification by independent third parties to ensure the long-term viability of carbon sequestration.
                    </p>
                </div>
            </div>
        </div>
    );
}

// Partnership Form Component
function PartnershipForm({ selectedTier }) {
    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        company: '',
        position: '',
        phone: '',
        companySize: '',
        interests: selectedTier || 'partnership',
        message: '',
        formType: 'partnership'
    });
    const [submitStatus, setSubmitStatus] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (selectedTier) {
            setFormData(prev => ({
                ...prev,
                interests: selectedTier
            }));
        }
    }, [selectedTier]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            // Format data to match the partnership service expected structure
            const submissionData = {
                contactName: formData.name,
                companyName: formData.company,
                email: formData.email,
                phone: formData.phone,
                companySize: formData.companySize,
                interests: formData.interests,
                message: formData.message,
                formType: formData.formType,
                position: formData.position
            };

            // Use the partnership service for form submission
            if (window.partnershipService) {
                await window.partnershipService.createPartnershipInquiry(submissionData);
                setSubmitStatus('success');
                
                // Reset form after successful submission
                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    position: '',
                    phone: '',
                    companySize: '',
                    interests: 'partnership',
                    message: '',
                    formType: 'partnership'
                });
            } else {
                throw new Error('Partnership service not available');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
            <h2 className="text-2xl font-bold mb-6">Become a Partner</h2>
            {submitStatus === 'success' ? (
                <div className="text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-500/30 to-green-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-check text-green-400 text-3xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
                    <p className="text-gray-300 mb-8 text-lg">
                        We've received your partnership inquiry. Our team will review your request and contact you within 24 hours to discuss partnership opportunities.
                    </p>
                    <button 
                        onClick={() => setSubmitStatus(null)}
                        className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white transition-all transform hover:scale-105"
                    >
                        Submit Another Inquiry
                    </button>
                </div>
            ) : submitStatus === 'error' ? (
                <div className="text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-500/30 to-red-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-exclamation-triangle text-red-400 text-3xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Error</h3>
                    <p className="text-gray-300 mb-8 text-lg">
                        There was an error submitting your partnership inquiry. Please try again or contact us directly.
                    </p>
                    <button 
                        onClick={() => setSubmitStatus(null)}
                        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-300 rounded-lg text-white transition-all transform hover:scale-105"
                    >
                        Try Again
                    </button>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-user text-blue-400 mr-2"></i> 
                                Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-envelope text-blue-400 mr-2"></i>
                                Business Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="your.email@company.com"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-building text-blue-400 mr-2"></i>
                                Company Name *
                            </label>
                            <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your company"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-briefcase text-blue-400 mr-2"></i>
                                Position/Title
                            </label>
                            <input
                                type="text"
                                name="position"
                                value={formData.position}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your role"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-phone text-blue-400 mr-2"></i>
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your phone number"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-users text-blue-400 mr-2"></i>
                                Company Size
                            </label>
                            <select
                                name="companySize"
                                value={formData.companySize}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                                <option value="">Select company size</option>
                                <option value="startup">Startup (1-10 employees)</option>
                                <option value="small">Small Business (11-50 employees)</option>
                                <option value="medium">Medium Business (51-200 employees)</option>
                                <option value="large">Large Enterprise (201-1000 employees)</option>
                                <option value="enterprise">Enterprise (1000+ employees)</option>
                            </select>
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium mb-2 flex items-center">
                            <i className="fas fa-comment text-blue-400 mr-2"></i>
                            Tell us about your partnership interests
                        </label>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={4}
                            className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Which products interest you? What are your sustainability goals? How can we work together?"
                        />
                    </div>
                    
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium text-lg transition-all transform hover:scale-105"
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center">
                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                Submitting...
                            </span>
                        ) : (
                            <span className="flex items-center justify-center">
                                <i className="fas fa-handshake mr-2"></i>
                                Submit Partnership Inquiry
                            </span>
                        )}
                    </button>
                </form>
            )}
        </div>
    );
}

// Carbon Calculator Component (simplified version)
function CarbonCalculator() {
    const [emissions, setEmissions] = React.useState('');
    const [offset, setOffset] = React.useState(null);

    const calculateOffset = () => {
        const tons = parseFloat(emissions);
        if (tons > 0) {
            setOffset({
                carbonCredits: Math.ceil(tons * 65),
                sequoiaStandard: Math.ceil(tons * 85),
                sequoiaEnhanced: Math.ceil(tons * 125)
            });
        }
    };

    return (
        <div>
            <div className="mb-6">
                <h3 className="font-bold text-lg mb-2">Calculate Your Carbon Offset Costs</h3>
                <p className="text-gray-300">Enter your annual CO₂ emissions in tons to see pricing for our offset solutions.</p>
            </div>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-2">
                        Annual CO₂ Emissions (tons)
                    </label>
                    <input
                        type="number"
                        value={emissions}
                        onChange={(e) => setEmissions(e.target.value)}
                        placeholder="e.g. 100"
                        className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                </div>
                <button
                    onClick={calculateOffset}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-300 rounded-lg text-white transition-all"
                >
                    Calculate Offset Costs
                </button>
                {offset && (
                    <div className="bg-gray-700/50 p-4 rounded-lg space-y-3">
                        <div className="flex justify-between">
                            <span>Carbon Credits:</span>
                            <span className="font-bold text-green-400">£{offset.carbonCredits}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Sequoia Standard (25yr):</span>
                            <span className="font-bold text-green-400">£{offset.sequoiaStandard}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Sequoia Enhanced (50yr):</span>
                            <span className="font-bold text-green-400">£{offset.sequoiaEnhanced}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Main Partner Page Component
function PartnerWithUsPage() {
    const [selectedOffering, setSelectedOffering] = React.useState(null);

    const handleOfferingSelect = (offeringType) => {
        setSelectedOffering(offeringType);
        // Scroll to form
        const formElement = document.getElementById('partnership-form');
        if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
            {/* Hero Section */}
            <section className="relative py-20 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10"></div>
                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center max-w-4xl mx-auto">
                        <h1 className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                            Partner with Us
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed">
                            Join leading organizations in making a meaningful impact on climate change. Partner with us to 
                            offset your carbon footprint through verified carbon credits and long-term sequoia forest conservation.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-6">
                            <a 
                                href="#carbon-credits-section" 
                                className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center"
                            >
                                <i className="fas fa-leaf mr-3"></i>
                                Explore Carbon Solutions
                            </a>
                            <a 
                                href="#partnership-form" 
                                className="px-8 py-4 bg-white text-gray-900 hover:bg-gray-100 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center"
                            >
                                <i className="fas fa-handshake mr-3"></i>
                                Become a Partner
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-16">
                <div className="container mx-auto px-6">
                    {/* Partner Benefits */}
                    <PartnerBenefits />

                    {/* Carbon Credits Section */}
                    <div id="carbon-credits-section" className="mb-24 py-8 bg-gray-800/30 backdrop-blur-sm rounded-2xl p-8 border-t-4 border-green-500 shadow-lg">
                        <div className="flex items-center justify-center mb-8">
                            <div className="w-20 h-20 bg-green-500/30 rounded-full flex items-center justify-center mr-4">
                                <i className="fas fa-leaf text-green-400 text-3xl"></i>
                            </div>
                            <h2 className="text-4xl font-bold">Carbon <span className="text-green-400">Credits</span></h2>
                        </div>
                        <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto text-lg">
                            Offset your carbon footprint with our high-quality carbon credits from verified sustainable technology projects.
                        </p>
                        <CarbonCreditsOffering onRequestInfo={handleOfferingSelect} />
                    </div>
                    
                    {/* Sequoia Tons Section */}
                    <div id="sequoia-tons-section" className="mb-24 py-8 bg-gray-800/30 backdrop-blur-sm rounded-2xl p-8 border-t-4 border-green-500 shadow-lg">
                        <div className="flex items-center justify-center mb-8">
                            <div className="w-20 h-20 bg-green-500/30 rounded-full flex items-center justify-center mr-4">
                                <i className="fas fa-tree text-green-400 text-3xl"></i>
                            </div>
                            <h2 className="text-4xl font-bold">Sequoia <span className="text-green-400">Tons</span></h2>
                        </div>
                        <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto text-lg">
                            Make a premium impact by supporting ancient sequoia forests with our Sequoia Tons program, offering guaranteed carbon sequestration across different time horizons.
                        </p>
                        <SequoiaTonsOffering onRequestInfo={handleOfferingSelect} />
                    </div>

                    {/* Form and Additional Info */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
                        <div id="partnership-form" className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                            <div className="bg-green-600/20 border-b border-green-600/30 p-4 text-center">
                                <h2 className="text-2xl font-bold text-white">Partnership Inquiry</h2>
                            </div>
                            <div className="p-8">
                                <div className="mb-8">
                                    <h3 className="font-bold text-lg mb-2">Start Your Sustainability Journey</h3>
                                    <p className="text-gray-300">Connect with our team to explore partnership opportunities and find the perfect carbon offset solution for your organization.</p>
                                </div>
                                <PartnershipForm selectedTier={selectedOffering} />
                            </div>
                        </div>
                        <div className="space-y-8">
                            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                                <div className="bg-blue-600/20 border-b border-blue-600/30 p-4 text-center">
                                    <h2 className="text-2xl font-bold text-white">Carbon Calculator</h2>
                                </div>
                                <div className="p-8">
                                    <CarbonCalculator />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Final CTA Section */}
                    <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl border border-green-500/30 shadow-xl p-10 mb-8">
                        <div className="text-center max-w-3xl mx-auto">
                            <h2 className="text-3xl font-bold mb-6">Ready to Make an Impact?</h2>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Join forward-thinking organizations that are taking meaningful action on climate change. Whether you need 
                                immediate carbon offsets or long-term sequestration commitments, we have the perfect solution for your sustainability goals.
                            </p>
                            <div className="flex flex-wrap justify-center gap-6">
                                <a 
                                    href="#carbon-credits-section" 
                                    className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white font-bold text-lg transition-all transform hover:scale-105 flex items-center"
                                >
                                    <i className="fas fa-leaf mr-3"></i>
                                    View Carbon Credits
                                </a>
                                <a 
                                    href="#partnership-form" 
                                    className="px-8 py-4 bg-white text-gray-900 hover:bg-gray-100 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center"
                                >
                                    <i className="fas fa-handshake mr-3"></i>
                                    Start Partnership
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

window.PartnerWithUsPage = PartnerWithUsPage;
export default PartnerWithUsPage;
